import { Fragment, useState } from 'react'
import type { GccOwnership, GccPayRateMatch, GccPayRateUse, GccValueCase as ValueCase, GccValueFigures, GccValueLedgerLine } from '@shared/types'
import { GCC_OWNERSHIPS, GCC_WAVE_BANDS, findValueCaseMismatches, firstBenefitWave, formatSourcingPair } from '@tn/shared'
import { GccTooltip } from './GccTooltip'
import { GCC_TOOLTIPS } from './gcc-tooltips'
import { Badge, Chip, OWNERSHIP_STYLE, TIER_STYLE, fmtFte2 } from './GccSourcingModel'

/** Whole US dollars, so every figure on the tab can be added up by hand. */
export function fmtUsdExact(usd: number): string {
  const sign = usd < 0 ? '−' : ''
  return `${sign}$${Math.abs(Math.round(usd)).toLocaleString('en-US')}`
}

export const OWNER_LABEL: Record<GccOwnership, string> = { 'Captive-GCC': 'Captive GCC', MSP: 'MSP', Retained: 'Retained' }

export const RATE_MATCH_LABEL: Record<GccPayRateMatch, string> = {
  exact: 'Exact title',
  band: 'Role band',
  floor: 'Analyst floor',
  missing: 'No rate',
}

/** Text colour classes for the AI and location savings, as in the export (WI 2487). */
const AI = 'gx-t-ai'
const LOC = 'gx-t-loc'

function Step({ label, value, sub, tip, tone }: { label: string; value: string; sub?: string; tip: string; tone?: string }) {
  return (
    <li className="gx-card gx-lift flex-1 min-w-[120px] px-3 py-2.5">
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
        <GccTooltip content={tip}>{label}</GccTooltip>
      </div>
      <div className={`text-lg font-semibold mt-0.5 ${tone ?? ''}`.trim()}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </li>
  )
}

const ownerSplit = (fte: Record<GccOwnership, number>) =>
  GCC_OWNERSHIPS.filter(o => fte[o] > 0).map(o => `${OWNER_LABEL[o]} ${fmtFte2(fte[o])}`).join(' · ') || '—'

/** The totals as the traceability chain: FTE first, then the value case it prices. */
export function Waterfall({ vc }: { vc: ValueCase }) {
  const t = vc.totals
  const split = vc.capabilities.flatMap(c => c.sourcingSplit)
  const pairs = new Map<string, number>()
  for (const a of split) {
    const key = formatSourcingPair(a.ownership, a.location)
    pairs.set(key, Math.round(((pairs.get(key) ?? 0) + a.fte) * 100) / 100)
  }
  return (
    <section aria-label="Value case waterfall" className="mb-6">
      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
        <GccTooltip content={GCC_TOOLTIPS.valueCaseWaterfall}>Traceability chain</GccTooltip>
      </p>
      <ol className="flex flex-wrap gap-2 mb-2">
        <Step label="Current FTE" value={fmtFte2(t.currentFte)} tip={GCC_TOOLTIPS.valueCaseCurrentFte} />
        <Step label="AI released FTE" value={`−${fmtFte2(t.aiReleasedFte)}`} tone={AI} tip={GCC_TOOLTIPS.valueCaseAiReleasedFte} />
        <Step label="Residual FTE" value={fmtFte2(t.residualFte)} tip={GCC_TOOLTIPS.valueCaseResidualFte} />
        <Step label="Sourcing split" value={`${pairs.size} pair${pairs.size !== 1 ? 's' : ''}`}
          sub={[...pairs].map(([k, v]) => `${k} ${fmtFte2(v)}`).join(' · ') || '—'} tip={GCC_TOOLTIPS.sourcingSplit} />
        <Step label="Future workforce" value={fmtFte2(GCC_OWNERSHIPS.reduce((s, o) => s + t.futureFteByOwnership[o], 0))}
          sub={ownerSplit(t.futureFteByOwnership)} tip={GCC_TOOLTIPS.valueCaseFutureFte} />
      </ol>
      <ol className="flex flex-wrap gap-2">
        <Step label="Current cost" value={fmtUsdExact(t.currentCostUSD)} tip={GCC_TOOLTIPS.valueCaseCurrentCost} />
        <Step label="AI saving" value={`−${fmtUsdExact(t.aiSavingUSD)}`} tone={AI} tip={GCC_TOOLTIPS.valueCaseAiSaving} />
        <Step label="Location saving" value={`−${fmtUsdExact(t.locationSavingUSD)}`} tone={LOC} tip={GCC_TOOLTIPS.valueCaseLocationSaving} />
        <Step label="Net value case" value={fmtUsdExact(t.totalSavingUSD)} tone="gx-t-good" sub="Run-rate saving per year" tip={GCC_TOOLTIPS.valueCaseTotalSaving} />
        <Step label="End-state cost" value={fmtUsdExact(t.endStateCostUSD)} tip={GCC_TOOLTIPS.valueCaseEndStateCost} />
      </ol>
    </section>
  )
}

export function Waves({ vc }: { vc: ValueCase }) {
  return (
    <section aria-label="Value case phasing" className="mb-6">
      <table className="gx-tbl w-full max-w-2xl text-xs">
        <thead>
          <tr className="text-left text-gray-400 uppercase tracking-wide border-b border-gray-100">
            <th className="pb-2 font-medium"><GccTooltip content={GCC_TOOLTIPS.valueCaseWaves}>Wave</GccTooltip></th>
            <th className="pb-2 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseWaveAiStart}>AI saving starts</GccTooltip></th>
            <th className="pb-2 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseWaveLocationStart}>Location saving starts</GccTooltip></th>
            <th className="pb-2 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseWaveRunRate}>Run-rate after wave</GccTooltip></th>
          </tr>
        </thead>
        <tbody>
          {vc.waves.map(w => (
            <tr key={w.wave} className="border-b border-gray-50">
              <td className="py-1.5 text-gray-700"><span className="font-semibold">Wave {w.wave}</span> <span className="text-gray-400">{GCC_WAVE_BANDS[w.wave].label}</span></td>
              <td className={`py-1.5 text-right font-semibold ${AI}`}>{fmtUsdExact(w.aiSavingUSD)}</td>
              <td className={`py-1.5 text-right font-semibold ${LOC}`}>{fmtUsdExact(w.locationSavingUSD)}</td>
              <td className="py-1.5 text-right font-semibold text-gray-800">{fmtUsdExact(w.cumulativeSavingUSD)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

const rate = (usd: number | null) => (usd === null ? 'No rate' : fmtUsdExact(usd))

function TaskLedger({ lines }: { lines: GccValueLedgerLine[] }) {
  return (
    <table className="gx-tbl w-full text-xs bg-gray-50 rounded">
      <thead>
        <tr className="text-left text-gray-400">
          <th className="px-3 py-1.5 font-medium"><GccTooltip content={GCC_TOOLTIPS.valueCaseLedgerTask}>Task</GccTooltip></th>
          <th className="px-3 py-1.5 font-medium"><GccTooltip content={GCC_TOOLTIPS.valueCaseLedgerDisposition}>Disposition</GccTooltip></th>
          <th className="px-3 py-1.5 font-medium"><GccTooltip content={GCC_TOOLTIPS.valueCaseLedgerTiers}>Today → end state</GccTooltip></th>
          <th className="px-3 py-1.5 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseLedgerFte}>FTE → residual</GccTooltip></th>
          <th className="px-3 py-1.5 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseLedgerRates}>Rate today → end</GccTooltip></th>
          <th className="px-3 py-1.5 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseLedgerAi}>AI</GccTooltip></th>
          <th className="px-3 py-1.5 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseLedgerLocation}>Location</GccTooltip></th>
          <th className="px-3 py-1.5 font-medium text-center"><GccTooltip content={GCC_TOOLTIPS.valueCaseLedgerFrom}>From</GccTooltip></th>
        </tr>
      </thead>
      <tbody>
        {lines.map(l => {
          const from = firstBenefitWave(l)
          return (
            <tr key={l.taskId}>
              <td className="px-3 py-1 text-gray-700">{l.taskName}<div className="text-[10px] text-gray-400">{l.roleTitle}</div></td>
              <td className="px-3 py-1 text-gray-600">{l.disposition}</td>
              <td className="px-3 py-1 text-gray-600 whitespace-nowrap">{l.currentTier} → {formatSourcingPair(l.ownership, l.location)}</td>
              <td className="px-3 py-1 text-right text-gray-600 whitespace-nowrap">{fmtFte2(l.baselineFte)} → {fmtFte2(l.residualFte)}</td>
              <td className="px-3 py-1 text-right text-gray-600 whitespace-nowrap">{rate(l.currentRateUSD)} → {rate(l.endRateUSD)}</td>
              <td className={`px-3 py-1 text-right font-medium ${AI}`}>{fmtUsdExact(l.aiSavingUSD)}</td>
              <td className={`px-3 py-1 text-right font-medium ${LOC}`}>{fmtUsdExact(l.locationSavingUSD)}</td>
              <td className="px-3 py-1 text-center text-gray-600" title={`AI from T-W${l.transformWave ?? 3}, location from M-W${l.migrationWave ?? 3}`}>
                {from ? `W${from}` : '—'}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function FigureCells({ f }: { f: GccValueFigures }) {
  return (
    <>
      <td className="py-3 text-right text-gray-600 align-top">{fmtUsdExact(f.currentCostUSD)}</td>
      <td className={`py-3 text-right font-semibold align-top ${AI}`}>{fmtUsdExact(f.aiSavingUSD)}</td>
      <td className={`py-3 text-right font-semibold align-top ${LOC}`}>{fmtUsdExact(f.locationSavingUSD)}</td>
      <td className="py-3 text-right font-semibold gx-t-good align-top">{fmtUsdExact(f.totalSavingUSD)}</td>
      <td className="py-3 text-right text-gray-600 align-top">{fmtUsdExact(f.endStateCostUSD)}</td>
    </>
  )
}

export function Capabilities({ vc }: { vc: ValueCase }) {
  const [openRow, setOpenRow] = useState<number | null>(null)
  return (
    <div className="gx-tbl-wrap mb-6">
      <table className="gx-tbl w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-400 border-b border-gray-100 uppercase tracking-wide">
            <th className="pb-3 font-medium"><GccTooltip content={GCC_TOOLTIPS.valueCaseCapability}>Capability</GccTooltip></th>
            <th className="pb-3 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseCurrentFte}>Current FTE</GccTooltip></th>
            <th className="pb-3 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseAiReleasedFte}>AI released</GccTooltip></th>
            <th className="pb-3 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseResidualFte}>Residual</GccTooltip></th>
            <th className="pb-3 font-medium pl-4 pr-6"><GccTooltip content={GCC_TOOLTIPS.sourcingSplit}>Sourcing split</GccTooltip></th>
            <th className="pb-3 font-medium"><GccTooltip content={GCC_TOOLTIPS.valueCaseFutureFte}>Future FTE</GccTooltip></th>
            <th className="pb-3 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseCurrentCost}>Current cost</GccTooltip></th>
            <th className="pb-3 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseAiSaving}>AI saving</GccTooltip></th>
            <th className="pb-3 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseLocationSaving}>Location saving</GccTooltip></th>
            <th className="pb-3 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseTotalSaving}>Total saving</GccTooltip></th>
            <th className="pb-3 font-medium text-right"><GccTooltip content={GCC_TOOLTIPS.valueCaseEndStateCost}>End-state cost</GccTooltip></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {vc.capabilities.map((c, i) => {
            const lines = vc.tasks.filter(t => t.capabilityName === c.capabilityName && t.functionScope === c.functionScope)
            const isOpen = openRow === i
            return (
              <Fragment key={i}>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 align-top">
                    <div className="font-semibold text-gray-800">{c.capabilityName}</div>
                    <div className="text-[10px] text-gray-400">{c.functionScope}</div>
                    {lines.length > 0 && (
                      <button onClick={() => setOpenRow(isOpen ? null : i)} aria-expanded={isOpen}
                        className="gx-link mt-1">
                        {isOpen ? 'Hide' : 'Show'} {lines.length} task{lines.length !== 1 ? 's' : ''}
                      </button>
                    )}
                  </td>
                  <td className="py-3 text-right text-gray-700 align-top">{fmtFte2(c.currentFte)}</td>
                  <td className={`py-3 text-right align-top ${AI}`}>−{fmtFte2(c.aiReleasedFte)}</td>
                  <td className="py-3 text-right font-semibold text-gray-800 align-top">{fmtFte2(c.residualFte)}</td>
                  <td className="py-3 pl-4 pr-6 align-top">
                    <ul className="space-y-0.5">
                      {c.sourcingSplit.map((a, j) => (
                        <li key={j} className="text-[11px] whitespace-nowrap">
                          <span className="gx-badge" style={{ background: OWNERSHIP_STYLE[a.ownership].bg, color: OWNERSHIP_STYLE[a.ownership].text }}>
                            {formatSourcingPair(a.ownership, a.location)}
                          </span> {fmtFte2(a.fte)}
                        </li>
                      ))}
                      {c.sourcingSplit.length === 0 && <li className="text-[11px] text-gray-400">No residual work</li>}
                    </ul>
                  </td>
                  <td className="py-3 text-[11px] text-gray-600 align-top whitespace-nowrap">{ownerSplit(c.futureFteByOwnership)}</td>
                  <FigureCells f={c} />
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={11} className="pb-3"><TaskLedger lines={lines} /></td>
                  </tr>
                )}
              </Fragment>
            )
          })}
          <tr className="border-t-2 border-gray-200 font-semibold">
            <td className="py-3 text-gray-800">Total</td>
            <td className="py-3 text-right text-gray-700">{fmtFte2(vc.totals.currentFte)}</td>
            <td className={`py-3 text-right ${AI}`}>−{fmtFte2(vc.totals.aiReleasedFte)}</td>
            <td className="py-3 text-right text-gray-800">{fmtFte2(vc.totals.residualFte)}</td>
            <td className="py-3" />
            <td className="py-3 text-[11px] text-gray-600 whitespace-nowrap">{ownerSplit(vc.totals.futureFteByOwnership)}</td>
            <FigureCells f={vc.totals} />
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const idList = (ids: string[]) => ids.join(', ')

type RateSortKey = 'tier' | 'roleTitle' | 'annualRateUSD' | 'match'
type SortDir = 'asc' | 'desc'

const RATE_PAGE_SIZE = 10
const RATE_COLUMNS: { key: RateSortKey; label: string; tip: string; className: string }[] = [
  { key: 'tier', label: 'Tier', tip: GCC_TOOLTIPS.valueCasePayTier, className: 'pr-4' },
  { key: 'roleTitle', label: 'Role', tip: GCC_TOOLTIPS.valueCasePayRole, className: 'pr-4' },
  { key: 'annualRateUSD', label: 'Annual rate', tip: GCC_TOOLTIPS.valueCasePayRate, className: 'pr-6 text-right' },
  { key: 'match', label: 'Match', tip: GCC_TOOLTIPS.valueCasePayMatch, className: '' },
]

/** Sorts by one column; a missing rate sorts after every priced one in either direction. */
function sortPayRates(rows: GccPayRateUse[], key: RateSortKey, dir: SortDir): GccPayRateUse[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    if (key !== 'annualRateUSD') {
      const label = (r: GccPayRateUse) => (key === 'match' ? RATE_MATCH_LABEL[r.match] : r[key])
      return sign * label(a).localeCompare(label(b))
    }
    if (a.annualRateUSD === null || b.annualRateUSD === null) return Number(a.annualRateUSD === null) - Number(b.annualRateUSD === null)
    return sign * (a.annualRateUSD - b.annualRateUSD)
  })
}

const FilterLabel = ({ children }: { children: string }) => (
  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">{children}</span>
)
const Divider = () => <span className="text-gray-200 mx-0.5">|</span>

/** The search, filters, sort and page of the pay-rate table. */
export interface PayRateSettings {
  query: string
  tier: string | null
  match: GccPayRateMatch | null
  sort: { key: RateSortKey; dir: SortDir } | null
  page: number
}

/**
 * What the pay-rate table shows for its settings: the tiers and matches it offers, the rows the search and
 * filters keep, sorted, and the page of them on screen. The page is kept within the pages there are.
 */
export function payRateView(rows: GccPayRateUse[], { query, tier, match, sort, page }: PayRateSettings) {
  const tiers = [...new Set(rows.map(r => r.tier))]
  const matches = (Object.keys(RATE_MATCH_LABEL) as GccPayRateMatch[]).filter(m => rows.some(r => r.match === m))
  const q = query.trim().toLowerCase()
  const filtered = rows.filter(r =>
    (!tier || r.tier === tier) && (!match || r.match === match) &&
    (!q || `${r.tier} ${r.roleTitle} ${RATE_MATCH_LABEL[r.match]}`.toLowerCase().includes(q)))
  const sorted = sort ? sortPayRates(filtered, sort.key, sort.dir) : filtered
  const totalPages = Math.max(1, Math.ceil(sorted.length / RATE_PAGE_SIZE))
  const current = Math.min(page, totalPages)
  return {
    tiers, matches, filtered, sorted, totalPages, current,
    pageRows: sorted.slice((current - 1) * RATE_PAGE_SIZE, current * RATE_PAGE_SIZE),
    hasFilter: !!q || !!tier || !!match,
  }
}

/** The pay rates the value case used: searchable, filterable by tier and match, sortable and paged. */
export function PayRateTable({ rows }: { rows: GccPayRateUse[] }) {
  const [query, setQuery] = useState('')
  const [tierFilter, setTierFilter] = useState<string | null>(null)
  const [matchFilter, setMatchFilter] = useState<GccPayRateMatch | null>(null)
  const [sort, setSort] = useState<{ key: RateSortKey; dir: SortDir } | null>(null)
  const [page, setPage] = useState(1)

  const { tiers, matches, filtered, sorted, totalPages, current, pageRows, hasFilter } =
    payRateView(rows, { query, tier: tierFilter, match: matchFilter, sort, page })

  const toggleSort = (key: RateSortKey) => {
    setSort(s => (s?.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))
    setPage(1)
  }
  const clearFilters = () => { setQuery(''); setTierFilter(null); setMatchFilter(null); setPage(1) }

  return (
    <div className="max-w-3xl mb-3">
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <input type="search" value={query} placeholder="Search role, tier or match" aria-label="Search pay rates"
          onChange={e => { setQuery(e.target.value); setPage(1) }}
          className="gx-search w-52 px-2.5 py-1 rounded-md border border-gray-200 text-xs text-gray-700" />
        <Divider />
        <FilterLabel>Tier</FilterLabel>
        {tiers.map(t => (
          <Chip key={t} label={t} active={tierFilter === t} style={TIER_STYLE[t]}
            onClick={() => { setTierFilter(p => (p === t ? null : t)); setPage(1) }} />
        ))}
        <Divider />
        <FilterLabel>Match</FilterLabel>
        {matches.map(m => (
          <Chip key={m} label={RATE_MATCH_LABEL[m]} active={matchFilter === m}
            onClick={() => { setMatchFilter(p => (p === m ? null : m)); setPage(1) }} />
        ))}
        {hasFilter && (
          <button onClick={clearFilters} className="gx-link ml-1">Clear</button>
        )}
        <span className="ml-auto text-gray-400 text-[11px]">
          {hasFilter ? `${filtered.length} of ${rows.length}` : `${rows.length} rate${rows.length !== 1 ? 's' : ''}`}
        </span>
      </div>
      <table className="gx-tbl w-full text-xs" aria-label="Pay rates">
        <thead>
          <tr className="text-left text-gray-400 uppercase tracking-wide border-b border-gray-100">
            {RATE_COLUMNS.map(c => {
              const dir = sort?.key === c.key ? sort.dir : null
              return (
                <th key={c.key} className={`pb-2 font-medium ${c.className}`}
                  aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}>
                  <button type="button" onClick={() => toggleSort(c.key)}
                    className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-600">
                    {c.label}
                    <span aria-hidden="true" className="text-[9px]">{dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '↕'}</span>
                  </button>
                  {/* Beside the sort button, not inside it, so focusing the badge never sorts (WI 2577) */}
                  {' '}<GccTooltip content={c.tip} />
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {pageRows.map((r, i) => {
            const ts = TIER_STYLE[r.tier]
            return (
              <tr key={`${r.tier}/${r.roleTitle}/${i}`} className="border-b border-gray-50">
                <td className="py-1 pr-4"><Badge label={r.tier} style={ts} /></td>
                <td className="py-1 pr-4 text-gray-700">{r.roleTitle}</td>
                <td className="py-1 pr-6 text-right text-gray-700 tabular-nums">{rate(r.annualRateUSD)}</td>
                <td className={`py-1 ${r.match === 'exact' ? 'text-gray-500' : 'text-amber-700'}`}>{RATE_MATCH_LABEL[r.match]}</td>
              </tr>
            )
          })}
          {pageRows.length === 0 && (
            <tr><td colSpan={4} className="py-3 text-center text-gray-400">No pay rates match the search or filters.</td></tr>
          )}
        </tbody>
      </table>
      {totalPages > 1 && (
        <nav aria-label="Pay rate pages" className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-gray-400">
            Showing {(current - 1) * RATE_PAGE_SIZE + 1}–{Math.min(current * RATE_PAGE_SIZE, sorted.length)} of {sorted.length}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(current - 1)} disabled={current === 1}
              className="text-[11px] text-gray-500 hover:text-gray-700 disabled:opacity-30 px-2 py-0.5 rounded hover:bg-gray-100 transition-colors">← Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button key={n} onClick={() => setPage(n)} aria-label={`Page ${n}`} aria-current={n === current ? 'page' : undefined}
                className={`w-5 h-5 text-[10px] rounded-full transition-colors ${n === current ? 'font-bold gx-page-on' : 'text-gray-400 hover:bg-gray-100'}`}>{n}</button>
            ))}
            <button onClick={() => setPage(current + 1)} disabled={current === totalPages}
              className="text-[11px] text-gray-500 hover:text-gray-700 disabled:opacity-30 px-2 py-0.5 rounded hover:bg-gray-100 transition-colors">Next →</button>
          </div>
        </nav>
      )}
    </div>
  )
}

export function Assumptions({ vc }: { vc: ValueCase }) {
  const a = vc.assumptions
  const mismatches = findValueCaseMismatches(vc)
  return (
    <section aria-label="Value case assumptions" className="text-xs text-gray-600">
      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
        <GccTooltip content={GCC_TOOLTIPS.valueCaseAssumptions}>Assumptions</GccTooltip>
      </p>
      <ul className="list-disc pl-4 space-y-1 mb-3">
        {a.notes.map((n, i) => <li key={i}>{n}</li>)}
        {a.defaultedLocationRoles.length > 0 && (
          <li className="text-amber-700">
            No current location given, so priced at HQ: {a.defaultedLocationRoles.join('; ')}.
          </li>
        )}
        {a.unpricedTaskIds.length > 0 && (
          <li className="text-amber-700">
            No current rate in the table, so left out of the cost lines: {idList(a.unpricedTaskIds)}.
          </li>
        )}
        {a.endRateFallbackTaskIds.length > 0 && (
          <li className="text-amber-700">
            No end-state rate in the table, so priced at today’s rate (no location saving): {idList(a.endRateFallbackTaskIds)}.
          </li>
        )}
      </ul>
      <PayRateTable rows={a.payRates} />
      <p className={mismatches.length === 0 ? 'gx-t-good' : 'text-red-700'}>
        {mismatches.length === 0
          ? 'Reconciliation: AI + location = total saving, and current − end-state cost = total saving, for every task, capability and the totals ✓'
          : `Reconciliation failed: ${mismatches.join('; ')}`}
      </p>
    </section>
  )
}

/**
 * Value Case tab (WI 2326): the Step 4 ledger as a traceability chain from current
 * FTE to the net value case, with AI and location savings kept apart, per
 * capability with a task drill-down, phased by wave, and its assumptions.
 */
export default function GccValueCase({ valueCase }: { valueCase: ValueCase | undefined }) {
  if (!valueCase) return <p className="text-sm text-gray-400">No value case data.</p>
  return (
    <div>
      <Waterfall vc={valueCase} />
      <Capabilities vc={valueCase} />
      <Waves vc={valueCase} />
      <Assumptions vc={valueCase} />
    </div>
  )
}
